/** Code-based route tree. We avoid the file-based code generator to
 * keep build simple — routes are declared explicitly here and compose
 * via createRootRouteWithContext → createRoute.
 *
 * Shape:
 *   /                  → redirects to /tabs/global
 *   /login             → login screen
 *   /tabs/:tabId       → Shell + <TabDispatcher> selects the body
 */
import { z } from "zod";
import {
  createRootRouteWithContext,
  createRoute,
  redirect,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { RootLayout } from "./__root";
import { LoginPage } from "./login";
import { TabPage } from "./tabs.$tabId";

export interface RouterContext {
  queryClient: QueryClient;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: z.object({
    next: z.string().optional(),
  }),
  component: LoginPage,
});

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/tabs/$tabId", params: { tabId: "global" } });
  },
});

export const tabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tabs/$tabId",
  validateSearch: z.object({
    period: z
      .enum(["week", "month", "month_current", "month_previous", "quarter", "all"])
      .optional(),
    metric: z
      .enum(["score", "messages", "conversations", "credits", "active_days"])
      .optional(),
    scope: z.enum(["all", "managers", "team"]).optional(),
  }),
  component: TabPage,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  tabRoute,
]);
